import type { PaymentV1 } from '@alpod-ai-writer/backend/src/schemas/payment/payment.schema';
import type { BillingResidency } from '@alpod-ai-writer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button, Spinner } from 'flowbite-react';
import { AlertTriangleIcon, CheckIcon, CreditCardIcon, LandmarkIcon } from 'lucide-react';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { Loading } from '../../../components/loading/loading.component';
import { getCurrencyFormatter } from '../../../lib/format.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { BillingForm } from '../../components/account/billingForm.component';
import { DataPrivacyModal } from '../../modals/dataPrivacy/dataPrivacyModal.component';
import { TermsAndConditionsModal } from '../../modals/termsAndConditions/termsAndConditionsModal.component';
import { useSession } from '../../hooks/useSession.hook';
import { RouteIndex } from '../../routeIndex';

const DEFAULT_BENEFITS = [
    'Plně naformátovaná šablona VŠ',
    'Import / export s možností úprav',
    'Kontrola formátu práce',
    '100 % správnost dle nařízení VŠ',
    'Průměrný čas zpracování: 4 minuty',
];

export interface DocumentPaymentProps {
    documentId: string;
}

export const DocumentPayment: FC<DocumentPaymentProps> = ({ documentId }) => {
    const queryClient = useQueryClient();
    const api = useApi();
    const navigate = useNavigate();

    const [editBillingInfo, setEditBillingInfo] = useState(false);
    const [paymentMethod, selectPaymentMethod] = useState<'bankTransfer' | 'gopay' | 'gpwebpay' | null>(null);
    const [showTac, setShowTac] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);

    const { identity } = useSession();

    const documentQuery = useQuery({
        queryKey: ['document', documentId],
        queryFn: async () => {
            return api.v1.documents.getById(documentId);
        }
    });

    const configQuery = useQuery({
        queryKey: ['config', 'payment'],
        queryFn: async () => {
            return api.v1.config.getPaymentConfig();
        }
    });

    const unlockSettingsQuery = useQuery({
        queryKey: ['config', 'document-unlock-settings'],
        queryFn: () => api.v1.config.getDocumentUnlockSettings(),
    });

    const createGoPayPayment = useMutation({
        mutationFn: (req: PaymentV1.GoPayPaymentRequest) => {
            return api.v1.documents.payWithGoPay(documentId, req);
        },
        onSuccess: (data) => {
            location.href = data.paymentUrl;
        }
    });

    const createGpWebpayPayment = useMutation({
        mutationFn: (req: PaymentV1.GpWebpayPaymentRequest) => {
            return api.v1.documents.payWithGpWebpay(documentId, req);
        },
        onSuccess: (data) => {
            location.href = data.paymentUrl;
        }
    });

    const billingResidency = identity.userFields['billingResidency'] as BillingResidency;
    const hasBillingInfo = !!billingResidency;
    const paymentConfig = configQuery.data && configQuery.data[billingResidency];

    useEffect(() => {
        if (!paymentConfig) return;
        const hasCard = paymentConfig.goPay?.enabled || paymentConfig.gpWebpay?.enabled;
        const hasBank = !!paymentConfig.bankTransfer;
        if (hasBank && !hasCard) {
            selectPaymentMethod('bankTransfer');
        }
    }, [paymentConfig]);

    const reload = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    }, [documentId]);

    const handleBillingInfoSaved = useCallback(() => {
        setEditBillingInfo(false);
    }, []);

    const handlePay = useCallback(() => {
        if (paymentMethod === 'bankTransfer') {
            navigate(pathWithParams(RouteIndex.documentDetail.bankTransferPayment, { documentId }));
        } else if (paymentMethod === 'gpwebpay') {
            createGpWebpayPayment.mutate({ residency: billingResidency });
        } else if (paymentMethod === 'gopay') {
            createGoPayPayment.mutate({ residency: billingResidency });
        }
    }, [paymentMethod, billingResidency, documentId, navigate, createGpWebpayPayment, createGoPayPayment]);

    const isPaymentPending = createGoPayPayment.isPending || createGpWebpayPayment.isPending;
    const canPay = !!paymentMethod && hasBillingInfo && !editBillingInfo;

    const czFormatter = configQuery.data ? getCurrencyFormatter(configQuery.data.CZ.currency, 'cs-CZ') : null;
    const skFormatter = configQuery.data ? getCurrencyFormatter(configQuery.data.SK.currency, 'cs-CZ') : null;
    const displayCZK = unlockSettingsQuery.data?.priceCZK ?? configQuery.data?.CZ.price;
    const displayEUR = unlockSettingsQuery.data?.priceEUR ?? configQuery.data?.SK.price;

    const priceLabel = paymentConfig && czFormatter && skFormatter && displayCZK != null && displayEUR != null
        ? billingResidency === 'SK'
            ? skFormatter.format(displayEUR)
            : czFormatter.format(displayCZK)
        : null;

    const isLoading = documentQuery.isLoading || configQuery.isLoading;

    return (
        <div className='max-w-2xl min-w-[32rem] mx-auto'>
            {!document && isLoading && (
                <div className='flex flex-row items-center justify-center py-12'>
                    <Loading label='Načítání dat...' />
                </div>
            )}

            <ErrorSwitch
                error={documentQuery.error}
                default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                options={[]}
                retry={reload}
            />

            {!isLoading && (
                <div className='flex flex-col gap-6'>

                    {/* Price card */}
                    {configQuery.data && czFormatter && skFormatter && displayCZK != null && displayEUR != null && (
                        <div className='border rounded-lg p-6 bg-white flex flex-col items-center gap-2'>
                            <div className='text-lg text-gray-800 font-semibold'>
                                {unlockSettingsQuery.data?.cardTitle ?? 'Formátování práce (CZ i SK)'}
                            </div>
                            <h2 className='text-2xl font-semibold mb-2'>
                                {czFormatter.format(displayCZK)} / {skFormatter.format(displayEUR)}
                            </h2>
                            {unlockSettingsQuery.data?.cardDescription && (
                                <p className='text-sm text-gray-600 text-center mb-2'>
                                    {unlockSettingsQuery.data.cardDescription}
                                </p>
                            )}
                            <ul className='flex flex-col items-start gap-2'>
                                {(unlockSettingsQuery.data?.benefits ?? DEFAULT_BENEFITS).map((benefit) => (
                                    <li key={benefit} className='flex flex-row items-start gap-2'>
                                        <CheckIcon size={20} className='text-lime-600' />
                                        <span className='leading-tight'>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Billing info */}
                    <div className='border rounded-lg p-4 bg-white'>
                        <div className='flex items-center justify-between mb-1'>
                            <h2 className='font-semibold text-gray-900'>Fakturační údaje</h2>
                            {hasBillingInfo && !editBillingInfo && (
                                <button
                                    type='button'
                                    className='hyperlink text-sm'
                                    onClick={() => setEditBillingInfo(true)}
                                >
                                    Upravit
                                </button>
                            )}
                        </div>

                        {(!hasBillingInfo || editBillingInfo) && (
                            <BillingForm
                                submitLabel='Uložit fakturační údaje'
                                showSuccessToast={false}
                                onSave={handleBillingInfoSaved}
                            />
                        )}

                        {hasBillingInfo && !editBillingInfo && (
                            <p className='text-sm text-gray-600'>
                                {identity.userFields['billingName']},&nbsp;
                                {identity.userFields['billingStreet']}{identity.userFields['billingStreetNumber'] ? ` ${identity.userFields['billingStreetNumber']}` : ''},&nbsp;
                                {identity.userFields['billingCity']},&nbsp;
                                {identity.userFields['billingPostcode']},&nbsp;
                                {billingResidency}
                            </p>
                        )}
                    </div>

                    {/* Payment method + order button */}
                    {hasBillingInfo && !editBillingInfo && (
                        <div className='border rounded-lg p-4 bg-white flex flex-col gap-4'>

                            <div>
                                <h2 className='font-semibold text-gray-900 mb-3'>Způsob platby</h2>
                                <div className='flex flex-row flex-wrap gap-2'>
                                    {paymentConfig?.gpWebpay?.enabled && (
                                        <PaymentMethodButton
                                            icon='card'
                                            label='Kartou online (GP WebPay)'
                                            isActive={paymentMethod === 'gpwebpay'}
                                            onClick={() => selectPaymentMethod('gpwebpay')}
                                        />
                                    )}
                                    {paymentConfig?.goPay?.enabled && (
                                        <PaymentMethodButton
                                            icon='card'
                                            label='Kartou online'
                                            isActive={paymentMethod === 'gopay'}
                                            onClick={() => selectPaymentMethod('gopay')}
                                        />
                                    )}
                                    {paymentConfig?.bankTransfer && (
                                        <PaymentMethodButton
                                            icon='bankTransfer'
                                            label='Bankovním převodem'
                                            isActive={paymentMethod === 'bankTransfer'}
                                            onClick={() => selectPaymentMethod('bankTransfer')}
                                        />
                                    )}
                                </div>

                                {!!paymentConfig && !paymentConfig.bankTransfer && !paymentConfig.goPay?.enabled && !paymentConfig.gpWebpay?.enabled && (
                                    <div className='flex items-center gap-2 p-3 border border-gray-300 rounded-lg mt-3'>
                                        <AlertTriangleIcon size={20} className='text-orange-600 flex-shrink-0' />
                                        <p className='text-sm font-medium text-gray-800'>
                                            Pro Vaše místo bydliště nejsou dostupné žádné platební metody.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <ErrorSwitch
                                error={createGpWebpayPayment.error ?? createGoPayPayment.error}
                                default={{ message: 'Při vytváření platby došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                                options={[]}
                            />

                            <Button
                                color='primary'
                                size='lg'
                                className='w-full'
                                onClick={handlePay}
                                disabled={!canPay || isPaymentPending}
                            >
                                {isPaymentPending ? (
                                    <>
                                        <Spinner size='sm' className='mr-2' />
                                        Zpracovávám...
                                    </>
                                ) : (
                                    priceLabel && paymentMethod
                                        ? `Objednat s povinností platby - ${priceLabel}`
                                        : 'Objednat s povinností platby'
                                )}
                            </Button>
                            <p className='text-xs text-gray-500 text-center'>
                                Potvrzením a úhradou objednávky souhlasíte s našimi{' '}
                                <button type='button' className='hyperlink' onClick={() => setShowTac(true)}>obchodními podmínkami</button>
                                {' '}a se{' '}
                                <button type='button' className='hyperlink' onClick={() => setShowPrivacy(true)}>zásadami ochrany osobních údajů</button>.
                            </p>
                        </div>
                    )}

                    {!paymentConfig && !configQuery.isLoading && (
                        <p className='text-sm text-gray-500'>Chyba při načítání konfigurace platby. Zkuste to prosím za chvíli znovu nebo kontaktujte podporu.</p>
                    )}
                </div>
            )}
        <TermsAndConditionsModal show={showTac} onClose={() => setShowTac(false)} />
        <DataPrivacyModal show={showPrivacy} onClose={() => setShowPrivacy(false)} />
        </div>
    );
};

const PaymentMethodButton: FC<{
    icon: 'card' | 'bankTransfer';
    label: string;
    isActive: boolean;
    onClick: () => void;
}> = ({ icon, label, isActive, onClick }) => (
    <button
        type='button'
        onClick={onClick}
        className={clsx(
            'flex items-center gap-2 py-2 px-4 rounded-md cursor-pointer transition-colors duration-150 border text-sm font-medium',
            isActive
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-transparent text-primary-500 border-gray-300 hover:border-primary-400'
        )}
    >
        {icon === 'card' && <CreditCardIcon size={20} />}
        {icon === 'bankTransfer' && <LandmarkIcon size={20} />}
        {label}
    </button>
);
